import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../../commonDesign/Button'
import SeriesListBuilder from './SeriesListBuilder'

const xAxisTypeOptions = [
  { label: 'Category', value: 'category' },
  { label: 'Value', value: 'value' },
  { label: 'Time', value: 'time' },
  { label: 'Log', value: 'log' }
]

const xAxisPositionOptions = [
  { label: 'Bottom', value: 'bottom' },
  { label: 'Top', value: 'top' }
]

const yAxisTypeOptions = [
  { label: 'Value', value: 'value' },
  { label: 'Log', value: 'log' }
]

const yAxisPositionOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' }
]

const tooltipTriggerOptions = [
  { label: 'Axis', value: 'axis' },
  { label: 'Item', value: 'item' }
]

const EMPTY_OBJ = {}
const EMPTY_ARR = []

function AxisChartEditor ({ config, columns, allPurposes, onChange }) {
  const xAxis = useMemo(() => config.xAxis || EMPTY_OBJ, [config.xAxis])
  const yAxes = useMemo(() => config.yAxes || EMPTY_ARR, [config.yAxes])
  const tooltip = useMemo(() => config.tooltip || EMPTY_OBJ, [config.tooltip])
  const series = useMemo(() => config.series || EMPTY_ARR, [config.series])

  const columnOptions = useMemo(() => {
    return [
      { label: 'Select a column...', value: '' },
      ...columns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns])

  // --- X-Axis handlers ---
  const handleXAxisChange = useCallback((field, value) => {
    onChange({ xAxis: { ...xAxis, [field]: value || null } })
  }, [onChange, xAxis])

  // --- Y-Axes handlers ---
  const handleYAxisChange = useCallback((idx, field, value) => {
    const updated = [...yAxes]
    updated[idx] = { ...updated[idx], [field]: value }
    onChange({ yAxes: updated })
  }, [onChange, yAxes])

  const handleAddYAxis = useCallback(() => {
    if (yAxes.length >= 4) return
    const id = yAxes.length === 0 ? 'left' : `y${yAxes.length}`
    const position = yAxes.some(y => y.position === 'right') ? 'left' : 'right'
    onChange({
      yAxes: [...yAxes, { axisId: id, type: 'value', format: null, name: null, position, padFactor: 0 }]
    })
  }, [onChange, yAxes])

  const handleRemoveYAxis = useCallback((idx) => {
    if (yAxes.length <= 1) return
    onChange({ yAxes: yAxes.filter((_, i) => i !== idx) })
  }, [onChange, yAxes])

  // --- Tooltip handlers ---
  const handleTooltipChange = useCallback((field, value) => {
    onChange({ tooltip: { ...tooltip, [field]: value || null } })
  }, [onChange, tooltip])

  // --- Series handler ---
  const handleSeriesChange = useCallback((updatedSeries) => {
    onChange({ series: updatedSeries })
  }, [onChange])

  return (
    <div>
      {/* X-Axis */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>X-Axis</h4>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <SydLabel label='Type'>
              <SydSelect
                minWidth='0'
                options={xAxisTypeOptions}
                value={xAxis.type || 'category'}
                onChange={(e) => handleXAxisChange('type', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Column (accessor)'>
              <SydSelect
                minWidth='0'
                options={columnOptions}
                value={xAxis.accessor || ''}
                onChange={(e) => handleXAxisChange('accessor', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Format'>
              <SydInput
                minWidth='0'
                value={xAxis.format || ''}
                onChange={(e) => handleXAxisChange('format', e.target.value)}
                placeholder='date'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Position'>
              <SydSelect
                minWidth='0'
                options={xAxisPositionOptions}
                value={xAxis.position || 'bottom'}
                onChange={(e) => handleXAxisChange('position', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Name'>
              <SydInput
                minWidth='0'
                value={xAxis.name || ''}
                onChange={(e) => handleXAxisChange('name', e.target.value)}
                placeholder='Optional axis name'
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>

      {/* Y-Axes */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>Y-Axes</h4>
          <SydButton
            size='sm'
            variant='ghost'
            onClick={handleAddYAxis}
            disabled={yAxes.length >= 4}
          >
            + Add Y-Axis
          </SydButton>
        </div>
        {yAxes.map((yAxis, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius-sm)',
              padding: '12px',
              marginBottom: '8px'
            }}
          >
            <Grid container spacing={2} alignItems='flex-end'>
              <Grid item xs={6} md={2}>
                <SydLabel label='Axis ID'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.axisId || ''}
                    onChange={(e) => handleYAxisChange(idx, 'axisId', e.target.value)}
                    placeholder='left'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Type'>
                  <SydSelect
                    minWidth='0'
                    options={yAxisTypeOptions}
                    value={yAxis.type || 'value'}
                    onChange={(e) => handleYAxisChange(idx, 'type', e.target.value)}
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Position'>
                  <SydSelect
                    minWidth='0'
                    options={yAxisPositionOptions}
                    value={yAxis.position || 'left'}
                    onChange={(e) => handleYAxisChange(idx, 'position', e.target.value)}
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Format'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.format || ''}
                    onChange={(e) => handleYAxisChange(idx, 'format', e.target.value)}
                    placeholder='marketValue'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Name'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.name || ''}
                    onChange={(e) => handleYAxisChange(idx, 'name', e.target.value)}
                    placeholder='Optional'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                {yAxes.length > 1 && (
                  <SydButton
                    size='sm'
                    variant='ghost'
                    onClick={() => handleRemoveYAxis(idx)}
                    style={{ color: 'var(--color-danger-500)' }}
                  >
                    Remove
                  </SydButton>
                )}
              </Grid>
            </Grid>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>Tooltip</h4>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <SydLabel label='Trigger'>
              <SydSelect
                minWidth='0'
                options={tooltipTriggerOptions}
                value={tooltip.trigger || 'axis'}
                onChange={(e) => handleTooltipChange('trigger', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Value Format'>
              <SydInput
                minWidth='0'
                value={tooltip.valueFormat || ''}
                onChange={(e) => handleTooltipChange('valueFormat', e.target.value)}
                placeholder='marketValue'
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>

      {/* Series */}
      <SeriesListBuilder
        series={series}
        columns={columns}
        yAxes={yAxes}
        allPurposes={allPurposes}
        onChange={handleSeriesChange}
      />
    </div>
  )
}

AxisChartEditor.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

export default AxisChartEditor
